import { Crown, Shield, Trash2, User } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useLeagueParticipants, useRemoveParticipant, useUpdateParticipantRole } from "@/modules/leagues/hooks/useLeagueAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { SettingsSubHeader } from "@/modules/leagues/components/SettingsSubHeader";
import { Avatar, AvatarFallback } from "@/components/atoms/Avatar";
import { Badge } from "@/components/atoms/Badge";
import { Skeleton } from "@/components/atoms/Skeleton";
import { useToast } from "@/hooks/use-toast";
import type { LeagueRole } from "@/modules/leagues/types/league";

const ROLE_LABEL: Record<LeagueRole, string> = { admin: "Administrador", moderator: "Moderador", member: "Membro" };
const ROLE_ICON: Record<LeagueRole, typeof Crown> = { admin: Crown, moderator: Shield, member: User };

export function LeagueSettingsParticipantsPage() {
  const { league } = useLeagueContext();
  const { firebaseUser } = useAuth();
  const { toast } = useToast();
  const { data: participants, isLoading } = useLeagueParticipants(league.id);
  const updateRole = useUpdateParticipantRole(league.id);
  const removeParticipant = useRemoveParticipant(league.id);
  const canManage = league.myRole === "admin" || league.myRole === "moderator";

  function initials(nome: string) {
    return nome.split(" ").slice(0, 2).map((n) => n[0]).join("");
  }

  function handleToggleModerator(uid: string, current: LeagueRole) {
    const next: LeagueRole = current === "moderator" ? "member" : "moderator";
    updateRole.mutate(
      { uid, role: next },
      { onSuccess: () => toast({ title: next === "moderator" ? "Promovido a moderador" : "Rebaixado a membro" }) }
    );
  }

  function handleRemove(uid: string, nome: string) {
    removeParticipant.mutate(uid, { onSuccess: () => toast({ title: `${nome} removido da liga` }) });
  }

  return (
    <div className="space-y-4">
      <SettingsSubHeader leagueId={league.id} title="Participantes" />
      {!canManage && (
        <p className="-mt-2 text-xs text-muted-foreground">Modo visualização — só o administrador pode alterar papéis ou remover alguém.</p>
      )}

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      <div className="space-y-2">
        {participants?.map((p) => {
          const RoleIcon = ROLE_ICON[p.role];
          const isSelf = p.uid === firebaseUser?.uid;
          return (
            <div key={p.uid} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-3">
              <Avatar size="sm">
                <AvatarFallback>{initials(p.nome)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{p.nome}</p>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <Badge variant={p.role === "admin" ? "primary" : "outline"}>
                    <RoleIcon className="h-3 w-3" /> {ROLE_LABEL[p.role]}
                  </Badge>
                  {p.status === "pendente" && <Badge variant="warning">Pendente</Badge>}
                  <span className="text-[11px] text-muted-foreground">{p.pontos} pts</span>
                </div>
              </div>

              {canManage && !isSelf && p.role !== "admin" && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleModerator(p.uid, p.role)}
                    title={p.role === "moderator" ? "Remover moderação" : "Promover a moderador"}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
                  >
                    <Shield className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleRemove(p.uid, p.nome)}
                    title="Remover participante"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
